import { RootState } from '@core/store/states/root.state'
import { createSelector, select } from '@ngrx/store'
import { hasCurrentLiveMatchByTeamId, mapTeamCardMatch } from '@pages/team/utils/team.utils'
import { TeamState } from '@pages/team/store/states/team.state'
import { selectLiveMatches } from '@core/store/selectors/socket.selectors'
import { parseSpiderChartDatasource } from '@shared/components/spider-chart/utils/spider-chart.utils'
import { Team } from '@core/models/team.models'
import { TeamHolder } from '@pages/team/models/team.models'
import { buildTeamComparisonHolder } from '@core/utils/team.utils'
import { Match } from '@core/models/match.models'
import { Competition, Season } from '@core/models/season-competition.models'
import { findSeasonAndCompetitionByID, mapCompetition } from '@core/utils/season.utils'
import {
  selectSelectionTeams,
  selectSelectionTeamsLoader
} from '@features/selections/store/selectors/selections.selectors'
import { buildTeamDualHeader } from '@pages/team/utils/team-dual-header.utils'
import {
  selectAggregation,
  selectAggregationsIsVsEnabled,
  selectAggregationsLeftAggregation,
  selectAggregationsModeList,
  selectAggregationsRightAggregation
} from '@features/aggregations/store/selectors/aggregations.selectors'
import { AggregationSelectable } from '@features/aggregations/models/aggregations.models'
import {
  AGGREGATIONS_AVERAGE_SELECTABLE_MODE,
  AGGREGATIONS_COMPETITION_AVERAGE_SELECTABLE_MODE
} from '@features/aggregations/constants/aggregation.constants'
import {
  selectCurrentSeasonByContext,
  selectSeasonsByContext
} from '@features/seasons/state/selectors/seasons.selectors'
import { pipe } from 'rxjs'
import { filter, map } from 'rxjs/operators'

export const selectTeamFeature = (state: RootState) => state?.team

export const selectTeamLoader = createSelector(selectTeamFeature, (state) => state?.teamLoading)

export const selectTeamB = createSelector(selectTeamFeature, (state: TeamState) => state?.teamB)

export const selectTeamSummary = createSelector(selectTeamFeature, (state: TeamState) => state?.summary)

export const selectTeamContext = createSelector(
  selectTeamSummary,
  selectSeasonsByContext('TeamSpace'),
  (team: Team, seasons: Season[]) => {
    const { season, competition } = findSeasonAndCompetitionByID(seasons, team?.seasonId, team?.competitionId)
    return {
      season,
      competition
    }
  }
)

export const selectTeamALoader = createSelector(selectTeamFeature, (state: TeamState) => state?.teamALoading)
export const selectTeamBLoader = createSelector(selectTeamFeature, (state: TeamState) => state?.teamBLoading)

export const selectIsTeamLoading = createSelector(
  selectSelectionTeamsLoader,
  selectTeamLoader,
  selectTeamALoader,
  (teamsLoading, teamLoading, teamALoading) => teamsLoading || teamLoading || teamALoading
)

export const selectTeamHolder = createSelector(
  selectTeamSummary,
  selectTeamB,
  selectAggregationsIsVsEnabled,
  (team: Team, teamB: Team, isVsEnabled): TeamHolder => {
    if (team) {
      return {
        team,
        comparison: isVsEnabled ? buildTeamComparisonHolder({ teamA: team, teamB }) : undefined
      }
    }
  }
)

export const selectTeamMetrics = createSelector(selectTeamFeature, (state) => state?.metrics)
export const selectTeamMetricsLoader = createSelector(selectTeamFeature, (state) => state?.metricsLoading)

export const selectTeamMetricsOverview = createSelector(selectTeamMetrics, (metrics) => metrics?.metricsOverview)
export const selectTeamMetricCategories = createSelector(selectTeamMetrics, (metrics) => metrics?.metricCategories)
export const selectTeamChartMetrics = createSelector(selectTeamMetrics, (metrics) => metrics?.chartMetricResults)

export const selectPlayers = createSelector(selectTeamFeature, (state: TeamState) => state?.players)
export const selectPlayersLoader = createSelector(selectTeamFeature, (state: TeamState) => state?.playersViewLoader)
export const selectTeamPlayersAggregation = createSelector(
  selectTeamFeature,
  (state: TeamState): AggregationSelectable => state?.playerAggregation
)

export const selectTeamSpiderChartDatasource = createSelector(selectTeamMetrics, (metrics) =>
  parseSpiderChartDatasource(metrics)
)

export const selectTeamSelectedView = createSelector(selectTeamFeature, (state: TeamState) => state?.selectedView)

export const selectTeamTabs = createSelector(selectTeamFeature, (state: TeamState) => state?.tabs)
export const selectTeamActiveTab = createSelector(selectTeamFeature, (state: TeamState) => state?.activeTabIndex)
export const selectTeamComparisonActiveTab = createSelector(
  selectTeamFeature,
  (state: TeamState) => state.comparisonActiveTabIndex
)
export const selectTeamActiveTabIndex = createSelector(
  selectTeamActiveTab,
  selectTeamComparisonActiveTab,
  selectAggregationsIsVsEnabled,
  (teamIdx, comparisonIdx, isVSEnabled) => (isVSEnabled ? comparisonIdx : teamIdx)
)

export const selectTeamLiveMatch = createSelector(
  selectTeamSummary,
  selectLiveMatches('TeamSpace'),
  (team: Team, updateLiveMatches: Match[]) => {
    if (team?.currentMatch || hasCurrentLiveMatchByTeamId(team?.id, updateLiveMatches)) {
      return mapTeamCardMatch(team?.currentMatch, team)
    }
  }
)

export const selectTeamLastMatch = createSelector(selectTeamSummary, (team: Team) =>
  mapTeamCardMatch(team?.lastMatch, team)
)

export const selectTeamNextMatch = createSelector(selectTeamSummary, (team: Team) =>
  mapTeamCardMatch(team?.nextMatch, team)
)

export const selectTeamDualHeader = createSelector(
  selectTeamHolder,
  selectAggregationsLeftAggregation,
  selectAggregationsRightAggregation,
  selectAggregation,
  selectAggregationsIsVsEnabled,
  selectSeasonsByContext('TeamSpace'),
  (holder, leftAggregation, rightAggregation, aggregation, isVsEnabled, seasons) =>
    buildTeamDualHeader(holder, leftAggregation, rightAggregation, aggregation, isVsEnabled, seasons)
)

export const selectTeamSpiderChartDualHeader = createSelector(
  selectTeamHolder,
  selectAggregationsIsVsEnabled,
  selectSeasonsByContext('TeamSpace'),
  (holder, isVsEnabled, seasons) =>
    buildTeamDualHeader(
      holder,
      AGGREGATIONS_AVERAGE_SELECTABLE_MODE,
      AGGREGATIONS_COMPETITION_AVERAGE_SELECTABLE_MODE,
      AGGREGATIONS_AVERAGE_SELECTABLE_MODE,
      isVsEnabled,
      seasons
    )
)

export const selectTeamCompetitions = pipe(
  select(selectCurrentSeasonByContext('TeamSpace')),
  filter((season) => !!season),
  map((season) => season.competitions.map((competition: Competition) => mapCompetition(competition, season)))
)

export const selectTeamAggregationsModes = createSelector(
  selectTeamHolder,
  selectSelectionTeams,
  selectAggregationsModeList,
  (teamHolder, selectionTeams, aggregationModes) => {
    if (teamHolder && selectionTeams) {
      const team = selectionTeams.find((team) => team.id === teamHolder.team.id)
      const aggregationAllowed = Object.keys(team?.aggregationDataSourceAllowed ?? {})
      return aggregationModes.filter((mode) => aggregationAllowed.includes(mode.id))
    }
  }
)
